import N3, { DataFactory } from 'n3';
import { Concept } from './Concept';
const { namedNode } = DataFactory;
const rdf = {
    namespace: 'http://www.w3.org/1999/02/22-rdf-syntax-ns#',
    type: 'http://www.w3.org/1999/02/22-rdf-syntax-ns#type',
}, rdfs = {
    label: 'http://www.w3.org/2000/01/rdf-schema#label',
    comment: 'http://www.w3.org/2000/01/rdf-schema#comment',
}, skos = {
    namespace: 'http://www.w3.org/2004/02/skos/core#',
    Concept: 'http://www.w3.org/2004/02/skos/core#Concept',
    ConceptScheme: 'http://www.w3.org/2004/02/skos/core#ConceptScheme',
    prefLabel: 'http://www.w3.org/2004/02/skos/core#prefLabel',
    altLabel: 'http://www.w3.org/2004/02/skos/core#altLabel',
    hiddenLabel: 'http://www.w3.org/2004/02/skos/core#hiddenLabel',
    broader: 'http://www.w3.org/2004/02/skos/core#broader',
    related: 'http://www.w3.org/2004/02/skos/core#related',
    narrower: 'http://www.w3.org/2004/02/skos/core#narrower',
    definition: 'http://www.w3.org/2004/02/skos/core#definition',
    scopeNote: 'http://www.w3.org/2004/02/skos/core#scopeNote',
    example: 'http://www.w3.org/2004/02/skos/core#example',
    note: 'http://www.w3.org/2004/02/skos/core#note',
    changeNote: 'http://www.w3.org/2004/02/skos/core#changeNote',
    editorialNote: 'http://www.w3.org/2004/02/skos/core#editorialNote',
    historyNote: 'http://www.w3.org/2004/02/skos/core#historyNote',
    topConceptOf: 'http://www.w3.org/2004/02/skos/core#topConceptOf',
    hasTopConcept: 'http://www.w3.org/2004/02/skos/core#hasTopConcept',
    Collection: 'http://www.w3.org/2004/02/skos/core#Collection',
    member: 'http://www.w3.org/2004/02/skos/core#member',
    notation: 'http://www.w3.org/2004/02/skos/core#notation',
}, isoThes = {
    namespace: 'http://purl.org/iso25964/skos-thes#',
    ConceptGroup: 'http://purl.org/iso25964/skos-thes#ConceptGroup',
    subGroup: 'http://purl.org/iso25964/skos-thes#subGroup',
    superGroup: 'http://purl.org/iso25964/skos-thes#superGroup',
};
/** ISO 639-2 (3-letter) to ISO 639-1 (2-letter) mapping for common languages. */
const iso639_2to1 = {
    ara: 'ar',
    zho: 'zh',
    chi: 'zh',
    ces: 'cs',
    cze: 'cs',
    dan: 'da',
    nld: 'nl',
    dut: 'nl',
    eng: 'en',
    fin: 'fi',
    fra: 'fr',
    fre: 'fr',
    deu: 'de',
    ger: 'de',
    ell: 'el',
    gre: 'el',
    heb: 'he',
    hin: 'hi',
    hun: 'hu',
    ind: 'id',
    ita: 'it',
    jpn: 'ja',
    kor: 'ko',
    may: 'ms',
    msa: 'ms',
    nor: 'no',
    nob: 'nb',
    nno: 'nn',
    pol: 'pl',
    por: 'pt',
    ron: 'ro',
    rum: 'ro',
    rus: 'ru',
    slk: 'sk',
    slo: 'sk',
    slv: 'sl',
    spa: 'es',
    swe: 'sv',
    tha: 'th',
    tur: 'tr',
    ukr: 'uk',
    vie: 'vi',
    cat: 'ca',
    eus: 'eu',
    baq: 'eu',
    glg: 'gl',
    hrv: 'hr',
    srp: 'sr',
    bul: 'bg',
    lit: 'lt',
    lav: 'lv',
    est: 'et',
};
/** Extract the preferred display label from a collection's multilingual labels. */
export function collectionDisplayLabel(collection, preferredLang = 'en') {
    const prefLabels = collection.labels.filter((l) => l.predicateUri.endsWith('#prefLabel'));
    const preferred = prefLabels.find((l) => l.language === preferredLang);
    if (preferred)
        return preferred.value;
    if (prefLabels.length > 0)
        return prefLabels[0].value;
    const uri = collection.uri;
    return uri.includes('#') ? uri.split('#').pop() : uri.split('/').pop() || uri;
}
/**
 * Extract the preferred display label from a concept's labels array.
 * Prefers English prefLabel, then any prefLabel, then URI local name.
 */
function getPrefLabel(labels, uri) {
    const prefLabels = labels.filter((l) => l.predicateUri.endsWith('#prefLabel'));
    const english = prefLabels.find((l) => l.language === 'en');
    if (english)
        return english.value;
    if (prefLabels.length > 0)
        return prefLabels[0].value;
    return uri.includes('#') ? uri.split('#').pop() : uri.split('/').pop() || uri;
}
/**
 * Manages a SKOS (Simple Knowledge Organization System) vocabulary backed by an N3 triple store.
 * Provides methods for creating, querying, and serializing concepts with hierarchical relationships.
 */
export default class Vocabulary {
    /**
     * Creates a new Vocabulary instance wrapping an N3 store.
     * @param store - The N3 triple store to use for storing vocabulary data
     */
    constructor(store) {
        Object.defineProperty(this, "store", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.store = store;
    }
    /**
     * Serializes the vocabulary to Turtle format.
     * @returns A promise that resolves to the Turtle-formatted string representation
     */
    toTurtle() {
        const writer = new N3.Writer({ prefixes: { rdf: rdf.namespace, skos: skos.namespace } });
        writer.addQuads(this.store.getQuads(null, null, null, null));
        return new Promise((resolve, reject) => {
            writer.end((error, result) => {
                if (error)
                    reject(error);
                else
                    resolve(result || '');
            });
        });
    }
    /**
     * Gets the first literal value for a subject-predicate pair, with language preference.
     * @param subject - The subject URI
     * @param predicate - The predicate URI
     * @param preferredLang - The preferred language tag (defaults to 'en')
     * @returns The literal value if found, or undefined if not found
     */
    getFirstLiteral(subject, predicate, preferredLang = 'en') {
        // Try preferred language first
        const quads = this.store.getQuads(subject, predicate, null, null);
        const preferred = quads.find((q) => q.object.language === preferredLang);
        if (preferred)
            return preferred.object.value;
        // Fallback to any literal
        const any = quads.find((q) => typeof q.object.value === 'string');
        return any ? any.object.value : undefined;
    }
    /**
     * Adds a triple to the store only if it doesn't already exist.
     * @param s - The subject URI
     * @param p - The predicate URI
     * @param o - The object URI
     */
    addQuadIfMissing(s, p, o) {
        const existing = this.store.getQuads(s, p, o, null);
        if (existing.length === 0) {
            this.store.addQuad(namedNode(s), namedNode(p), namedNode(o));
        }
    }
    /**
     * Replaces all existing literal values for a subject-predicate pair with a new value.
     * @param s - The subject URI
     * @param p - The predicate URI
     * @param value - The new literal value
     * @param lang - The language tag for the literal (defaults to 'en')
     */
    addLiteralQuadReplaceAll(s, p, value, lang = 'en') {
        const existing = this.store.getQuads(namedNode(s), namedNode(p), null, null);
        existing.forEach((q) => this.store.removeQuad(q));
        this.store.addQuad(namedNode(s), namedNode(p), DataFactory.literal(value, lang));
    }
    /**
     * Checks if a concept with the given URI exists in the vocabulary.
     * @param uri - The URI to check
     * @returns True if the URI is typed as a skos:Concept, false otherwise
     */
    hasConcept(uri) {
        return this.store.getQuads(uri, rdf.type, skos.Concept, null).length > 0;
    }
    /**
     * Converts the vocabulary to a JSON representation suitable for chat/API responses.
     * @returns A TVocabulary object containing all concepts as JSON
     */
    toLegacyJSON() {
        return {
            concepts: this.getConcepts().map((concept) => concept.toJSON()),
        };
    }
    /**
     * Builds a complete JSON representation of the vocabulary using bulk N3 queries.
     * Uses ~10 store scans instead of N*7 per-concept queries, making it O(total quads)
     * instead of O(concepts * relationships). Concepts are sorted by prefLabel.
     *
     * @param lang - Optional language filter. When provided, only literals with this
     *               language tag (or no language tag) are included.
     */
    toVocabularyJSON(lang) {
        const matchesLang = (literalLang) => {
            if (!lang)
                return true;
            return !literalLang || literalLang === lang;
        };
        // 1. Collect all concept URIs
        const conceptUriSet = new Set();
        for (const q of this.store.getQuads(null, rdf.type, skos.Concept, null)) {
            conceptUriSet.add(q.subject.value);
        }
        // 2. Bulk collect all labels (prefLabel, altLabel, hiddenLabel, rdfs:label)
        const labelsMap = new Map();
        const labelPredicates = [skos.prefLabel, skos.altLabel, skos.hiddenLabel, rdfs.label];
        for (const predicateUri of labelPredicates) {
            for (const q of this.store.getQuads(null, predicateUri, null, null)) {
                const uri = q.subject.value;
                if (!conceptUriSet.has(uri))
                    continue;
                const literal = q.object;
                const literalLang = literal.language || undefined;
                if (!matchesLang(literalLang))
                    continue;
                const arr = labelsMap.get(uri) || [];
                arr.push({ value: literal.value, predicateUri, language: literalLang });
                labelsMap.set(uri, arr);
            }
        }
        // 3. Bulk collect all definitions/notes
        const definitionsMap = new Map();
        const definitionPredicates = [
            skos.definition,
            skos.scopeNote,
            skos.example,
            skos.note,
            skos.changeNote,
            skos.editorialNote,
            skos.historyNote,
            rdfs.comment,
        ];
        for (const predicateUri of definitionPredicates) {
            for (const q of this.store.getQuads(null, predicateUri, null, null)) {
                const uri = q.subject.value;
                if (!conceptUriSet.has(uri))
                    continue;
                const literal = q.object;
                const literalLang = literal.language || undefined;
                if (!matchesLang(literalLang))
                    continue;
                const arr = definitionsMap.get(uri) || [];
                arr.push({ value: literal.value, predicateUri, language: literalLang });
                definitionsMap.set(uri, arr);
            }
        }
        // 4. Bulk collect broader relationships (+ inverse broader for narrower deduction)
        const broaderMap = new Map();
        const narrowerMap = new Map();
        for (const q of this.store.getQuads(null, skos.broader, null, null)) {
            const subj = q.subject.value;
            const obj = q.object.value;
            if (conceptUriSet.has(subj) && conceptUriSet.has(obj)) {
                (broaderMap.get(subj) || broaderMap.set(subj, new Set()).get(subj)).add(obj);
                // Inverse: obj has narrower subj
                (narrowerMap.get(obj) || narrowerMap.set(obj, new Set()).get(obj)).add(subj);
            }
        }
        for (const q of this.store.getQuads(null, skos.narrower, null, null)) {
            const subj = q.subject.value;
            const obj = q.object.value;
            if (conceptUriSet.has(subj) && conceptUriSet.has(obj)) {
                (narrowerMap.get(subj) || narrowerMap.set(subj, new Set()).get(subj)).add(obj);
            }
        }
        const relatedMap = new Map();
        for (const q of this.store.getQuads(null, skos.related, null, null)) {
            const subj = q.subject.value;
            const obj = q.object.value;
            if (conceptUriSet.has(subj) && conceptUriSet.has(obj)) {
                (relatedMap.get(subj) || relatedMap.set(subj, new Set()).get(subj)).add(obj);
                // skos:related is symmetric — ensure both directions
                (relatedMap.get(obj) || relatedMap.set(obj, new Set()).get(obj)).add(subj);
            }
        }
        // 5. Bulk collect inScheme
        const inSchemeMap = new Map();
        const inSchemeUri = `${skos.namespace}inScheme`;
        for (const q of this.store.getQuads(null, inSchemeUri, null, null)) {
            const uri = q.subject.value;
            if (conceptUriSet.has(uri)) {
                inSchemeMap.set(uri, q.object.value);
            }
        }
        // 5b. Bulk collect notation
        const notationMap = new Map();
        for (const q of this.store.getQuads(null, skos.notation, null, null)) {
            const uri = q.subject.value;
            if (conceptUriSet.has(uri)) {
                notationMap.set(uri, q.object.value);
            }
        }
        // 6. Build concepts array (sorted by prefLabel)
        const concepts = [];
        for (const uri of conceptUriSet) {
            concepts.push({
                uri,
                inScheme: inSchemeMap.get(uri),
                labels: labelsMap.get(uri) || [],
                definitions: definitionsMap.get(uri) || [],
                notation: notationMap.get(uri),
                broader: Array.from(broaderMap.get(uri) || []),
                narrower: Array.from(narrowerMap.get(uri) || []),
                related: Array.from(relatedMap.get(uri) || []),
            });
        }
        concepts.sort((a, b) => {
            const aLabel = getPrefLabel(a.labels, a.uri);
            const bLabel = getPrefLabel(b.labels, b.uri);
            return aLabel.localeCompare(bLabel);
        });
        // 7. Build scheme metadata
        let scheme = null;
        const schemeUri = this.getConceptSchemeUri();
        if (schemeUri) {
            const dcterms = {
                title: 'http://purl.org/dc/terms/title',
                description: 'http://purl.org/dc/terms/description',
                created: 'http://purl.org/dc/terms/created',
                modified: 'http://purl.org/dc/terms/modified',
                creator: 'http://purl.org/dc/terms/creator',
                publisher: 'http://purl.org/dc/terms/publisher',
                license: 'http://purl.org/dc/terms/license',
                rights: 'http://purl.org/dc/terms/rights',
                source: 'http://purl.org/dc/terms/source',
            };
            const schemeLiterals = this.getSubjectLiterals(schemeUri, [
                skos.prefLabel,
                rdfs.label,
                dcterms.title,
                dcterms.description,
                dcterms.created,
                dcterms.modified,
                dcterms.creator,
                dcterms.publisher,
                dcterms.license,
                dcterms.rights,
                dcterms.source,
            ]).filter((l) => matchesLang(l.language));
            const getFirst = (predicate) => schemeLiterals.find((l) => l.predicateUri === predicate)?.value;
            const label = getFirst(skos.prefLabel) ||
                getFirst(rdfs.label) ||
                getFirst(dcterms.title) ||
                (schemeUri.includes('#') ? schemeUri.split('#').pop() : schemeUri.split('/').pop() || schemeUri);
            scheme = {
                uri: schemeUri,
                prefLabel: label,
                description: getFirst(dcterms.description),
                created: getFirst(dcterms.created),
                modified: getFirst(dcterms.modified),
                creator: getFirst(dcterms.creator),
                publisher: getFirst(dcterms.publisher),
                license: getFirst(dcterms.license),
                rights: getFirst(dcterms.rights),
                source: getFirst(dcterms.source),
                topConcepts: this.getTopConcepts(schemeUri),
            };
        }
        // 8. Build collections (filter literals by lang)
        const collections = this.getCollections().map((c) => ({
            uri: c.uri,
            labels: c.labels.filter((l) => matchesLang(l.language)),
            notes: c.notes.filter((l) => matchesLang(l.language)),
            notation: c.notation,
            members: c.memberUris,
            superGroup: c.superGroupUri,
        }));
        return { scheme, concepts, collections };
    }
    /**
     * Creates a new Vocabulary instance from Turtle-formatted data.
     * @param turtleData - The Turtle-formatted RDF data to parse
     * @returns A promise that resolves to a new Vocabulary instance
     */
    static fromTurtle(turtleData) {
        return new Promise((resolve, reject) => {
            const store = new N3.Store();
            const parser = new N3.Parser();
            parser.parse(turtleData, (error, quad, _prefixes) => {
                if (error) {
                    reject(error);
                }
                else if (quad) {
                    store.addQuad(quad);
                }
                else {
                    resolve(new Vocabulary(store));
                }
            });
        });
    }
    /**
     * Retrieves a Concept object for the given URI.
     * Extracts prefLabel, definition, broader, and narrower relationships from the store.
     * Narrower relations are deduced both from explicit narrower triples and inverse broader triples.
     * @param uri - The URI of the concept to retrieve
     * @returns A Concept instance populated with data from the store
     */
    getConcept(uri) {
        const conceptName = this.getFirstLiteral(uri, skos.prefLabel) ||
            (uri.includes('#') ? uri.split('#').pop() : uri.split('/').pop() || uri);
        const broaderRelations = this.store.getQuads(uri, skos.broader, null, null);
        const narrowerRelations = this.store.getQuads(uri, skos.narrower, null, null);
        // deduce narrower relations based on broader relations
        const inverseBroader = this.store.getQuads(null, skos.broader, uri, null);
        const broader = Array.from(new Set(broaderRelations.map((q) => q.object.value).filter((target) => this.hasConcept(target))));
        const narrowers = new Set(narrowerRelations.map((q) => q.object.value).filter((target) => this.hasConcept(target)));
        inverseBroader.forEach((q) => {
            const subj = q.subject.value;
            if (this.hasConcept(subj))
                narrowers.add(subj);
        });
        const definition = this.getFirstLiteral(uri, skos.definition) || 'No definition provided';
        const inSchemeQuads = this.store.getQuads(uri, `${skos.namespace}inScheme`, null, null);
        const inScheme = inSchemeQuads.length > 0 ? inSchemeQuads[0].object.value : undefined;
        return new Concept(uri, conceptName, broader, Array.from(narrowers), definition, inScheme);
    }
    /**
     * Retrieves all concepts in the vocabulary.
     * @returns An array of all Concept instances typed as skos:Concept in the store
     */
    getConcepts() {
        const skosConcepts = this.store.getQuads(null, rdf.type, skos.Concept, null);
        return skosConcepts.map((skosConcept) => this.getConcept(skosConcept.subject.value));
    }
    /**
     * Adds a concept to the vocabulary.
     * Creates the concept type triple, prefLabel, definition (if provided), and
     * establishes broader/narrower relationships with existing concepts.
     * @param concept - The Concept instance to add
     */
    addConcept(concept) {
        this.addQuadIfMissing(concept.uri, rdf.type, skos.Concept);
        this.addLiteralQuadReplaceAll(concept.uri, skos.prefLabel, concept.prefLabel, 'en');
        // Only add a definition triple if a non-empty definition was provided
        if (concept.definition && concept.definition.trim() !== '') {
            this.addLiteralQuadReplaceAll(concept.uri, skos.definition, concept.definition, 'en');
        }
        // Broader relations and inverse narrower (only if target already exists as skos:Concept)
        concept.broader.forEach((broader) => {
            if (this.hasConcept(broader)) {
                this.addQuadIfMissing(concept.uri, skos.broader, broader);
                this.addQuadIfMissing(broader, skos.narrower, concept.uri);
            }
        });
        // Narrower relations and inverse broader (only if target already exists as skos:Concept)
        concept.narrower.forEach((narrower) => {
            if (this.hasConcept(narrower)) {
                this.addQuadIfMissing(concept.uri, skos.narrower, narrower);
                this.addQuadIfMissing(narrower, skos.broader, concept.uri);
            }
        });
    }
    /**
     * Updates the definition of an existing concept.
     * @param conceptUri - The URI of the concept to update
     * @param newDefinition - The new definition text
     */
    updateConceptDefinition(conceptUri, newDefinition) {
        this.addLiteralQuadReplaceAll(conceptUri, skos.definition, newDefinition, 'en');
    }
    /**
     * Updates the preferred label of an existing concept.
     * @param conceptUri - The URI of the concept to update
     * @param newPrefLabel - The new preferred label
     * @param lang - The language tag for the label (defaults to 'en')
     */
    updateConceptPrefLabel(conceptUri, newPrefLabel, lang = 'en') {
        this.addLiteralQuadReplaceAll(conceptUri, skos.prefLabel, newPrefLabel, lang);
    }
    /**
     * Adds a symmetric skos:related relationship between two concepts.
     * @param conceptUri - The URI of the first concept
     * @param relatedUri - The URI of the related concept
     */
    addRelated(conceptUri, relatedUri) {
        // skos:related is symmetric
        this.addQuadIfMissing(conceptUri, skos.related, relatedUri);
        this.addQuadIfMissing(relatedUri, skos.related, conceptUri);
    }
    /**
     * Removes a concept and all its relationships from the vocabulary.
     * Deletes all triples where the concept is either subject or object.
     * @param conceptUri - The URI of the concept to remove
     */
    removeConcept(conceptUri) {
        // Remove all triples where subject = conceptUri
        const subjectQuads = this.store.getQuads(namedNode(conceptUri), null, null, null);
        subjectQuads.forEach((q) => this.store.removeQuad(q));
        // Remove all triples where object = conceptUri (cleanup inverse relations)
        const objectQuads = this.store.getQuads(null, null, namedNode(conceptUri), null);
        objectQuads.forEach((q) => this.store.removeQuad(q));
    }
    /**
     * Generates a D3-compatible graph structure for visualization.
     * Creates nodes for all concepts and links for broader, narrower, and related relationships.
     * @returns An object with nodes array and links array suitable for D3 force-directed graphs
     */
    getD3Graph() {
        const nodeMap = new Map();
        const links = [];
        const linkSet = new Set();
        const skosConcepts = this.store.getQuads(null, rdf.type, skos.Concept, null);
        const addNode = (uri) => {
            if (!nodeMap.has(uri)) {
                const name = this.getFirstLiteral(uri, skos.prefLabel) ||
                    (uri.includes('#') ? uri.split('#').pop() : uri.split('/').pop() || uri);
                nodeMap.set(uri, { id: uri, name, group: 1 });
            }
        };
        const addLink = (source, target, value) => {
            const key = `${source}|${value}|${target}`;
            if (!linkSet.has(key)) {
                linkSet.add(key);
                links.push({ source, target, value });
            }
        };
        skosConcepts.forEach((skosConcept) => {
            const conceptUri = skosConcept.subject.value;
            addNode(conceptUri);
            // broader
            this.store.getQuads(conceptUri, skos.broader, null, null).forEach((q) => {
                const target = q.object.value;
                if (this.hasConcept(target)) {
                    addNode(target);
                    addLink(conceptUri, target, 'broader');
                }
            });
            // narrower
            this.store.getQuads(conceptUri, skos.narrower, null, null).forEach((q) => {
                const target = q.object.value;
                if (this.hasConcept(target)) {
                    addNode(target);
                    addLink(conceptUri, target, 'narrower');
                }
            });
            // related
            this.store.getQuads(conceptUri, skos.related, null, null).forEach((q) => {
                const target = q.object.value;
                addNode(target);
                addLink(conceptUri, target, 'related');
            });
        });
        return {
            nodes: Array.from(nodeMap.values()),
            links,
        };
    }
    /**
     * Gets all labels for a concept (prefLabel, altLabel, hiddenLabel, rdfs:label).
     * @param conceptUri - The URI of the concept
     * @returns Array of literal values with predicate URI and optional language tag
     */
    getConceptLabels(conceptUri) {
        const labels = [];
        const labelPredicates = [skos.prefLabel, skos.altLabel, skos.hiddenLabel, rdfs.label];
        for (const predicateUri of labelPredicates) {
            const quads = this.store.getQuads(conceptUri, predicateUri, null, null);
            for (const quad of quads) {
                const literal = quad.object;
                labels.push({
                    value: literal.value,
                    predicateUri,
                    language: literal.language || undefined,
                });
            }
        }
        return labels;
    }
    /**
     * Gets all definitions/notes for a concept (definition, scopeNote, example, note, rdfs:comment).
     * @param conceptUri - The URI of the concept
     * @returns Array of literal values with predicate URI and optional language tag
     */
    getConceptDefinitions(conceptUri) {
        const definitions = [];
        const definitionPredicates = [
            skos.definition,
            skos.scopeNote,
            skos.example,
            skos.note,
            skos.changeNote,
            skos.editorialNote,
            skos.historyNote,
            rdfs.comment,
        ];
        for (const predicateUri of definitionPredicates) {
            const quads = this.store.getQuads(conceptUri, predicateUri, null, null);
            for (const quad of quads) {
                const literal = quad.object;
                definitions.push({
                    value: literal.value,
                    predicateUri,
                    language: literal.language || undefined,
                });
            }
        }
        return definitions;
    }
    /**
     * Gets the skos:notation value for a concept (or collection), if present.
     * Language-less by SKOS convention. When multiple notation triples exist,
     * the first one (insertion order) wins — matches the collection notation
     * extraction in getCollections().
     * @param subjectUri - URI of the concept or collection
     * @returns The notation literal value, or undefined when absent
     */
    getConceptNotation(subjectUri) {
        const quads = this.store.getQuads(subjectUri, skos.notation, null, null);
        return quads.length > 0 ? quads[0].object.value : undefined;
    }
    /**
     * Gets the ConceptScheme URI if one exists in the vocabulary.
     * @returns The URI of the first ConceptScheme found, or undefined if none exists
     */
    getConceptSchemeUri() {
        const schemeQuads = this.store.getQuads(null, rdf.type, skos.ConceptScheme, null);
        return schemeQuads.length > 0 ? schemeQuads[0].subject.value : undefined;
    }
    /**
     * Returns the list of languages available in this vocabulary.
     * Prefers dct:language on the ConceptScheme (authoritative, e.g. UNESCO Thesaurus).
     * Falls back to scanning distinct language tags from actual literals.
     */
    getAvailableLanguages() {
        // 1. Scan actual literal tags (authoritative — these are exactly what
        //    the lang filter compares against in toVocabularyJSON). Prefer this
        //    over dct:language declarations because some publishers put
        //    friendly names ("English", "Italiano") in dct:language while the
        //    real literal tags use ISO codes ("@en", "@it"). Returning friendly
        //    names from the dropdown then breaks server-side filtering.
        const langs = new Set();
        const predicates = [
            skos.prefLabel,
            skos.altLabel,
            skos.hiddenLabel,
            rdfs.label,
            skos.definition,
            skos.scopeNote,
            skos.example,
            skos.note,
            skos.changeNote,
            skos.editorialNote,
            skos.historyNote,
            rdfs.comment,
        ];
        for (const predicateUri of predicates) {
            for (const q of this.store.getQuads(null, predicateUri, null, null)) {
                const lang = q.object.language;
                if (lang)
                    langs.add(lang);
            }
        }
        if (langs.size > 0) {
            return Array.from(langs).sort();
        }
        // 2. Fallback: dct:language declaration on the ConceptScheme.
        //    Only used when no language-tagged literals exist (rare).
        const dctLanguage = 'http://purl.org/dc/terms/language';
        const schemeUri = this.getConceptSchemeUri();
        if (schemeUri) {
            const declared = this.store.getQuads(schemeUri, dctLanguage, null, null);
            if (declared.length > 0) {
                const declaredLangs = new Set();
                for (const q of declared) {
                    const val = q.object.value;
                    // Value can be a language tag literal ("en") or a URI (http://id.loc.gov/vocabulary/iso639-2/eng)
                    const match = val.match(/iso639-[12]\/([a-z]{2,3})$/);
                    const code = match ? match[1] : val;
                    declaredLangs.add(iso639_2to1[code] || code);
                }
                return Array.from(declaredLangs).sort();
            }
        }
        return [];
    }
    /**
     * Gets literal values for specified predicates on a subject.
     * @param subjectUri - The URI of the subject
     * @param predicateUris - Array of predicate URIs to query
     * @returns Array of literal values with predicate URI and optional language tag
     */
    getSubjectLiterals(subjectUri, predicateUris) {
        const literals = [];
        for (const predicateUri of predicateUris) {
            const quads = this.store.getQuads(subjectUri, predicateUri, null, null);
            for (const quad of quads) {
                const literal = quad.object;
                literals.push({
                    value: literal.value,
                    predicateUri,
                    language: literal.language || undefined,
                });
            }
        }
        return literals;
    }
    /**
     * Gets top concept URIs for a ConceptScheme (via topConceptOf or hasTopConcept).
     * @param schemeUri - The URI of the ConceptScheme
     * @returns Array of concept URIs that are top concepts of the scheme
     */
    getTopConcepts(schemeUri) {
        const topConcepts = new Set();
        // Check for concepts with skos:topConceptOf pointing to this scheme
        const topConceptOfQuads = this.store.getQuads(null, skos.topConceptOf, schemeUri, null);
        for (const quad of topConceptOfQuads) {
            topConcepts.add(quad.subject.value);
        }
        // Also check for skos:hasTopConcept on the scheme itself
        const hasTopConceptQuads = this.store.getQuads(schemeUri, skos.hasTopConcept, null, null);
        for (const quad of hasTopConceptQuads) {
            topConcepts.add(quad.object.value);
        }
        return Array.from(topConcepts);
    }
    /**
     * Retrieves all SKOS relationships for a given concept URI.
     * Returns broader, narrower, related, exactMatch, and closeMatch relationships
     * where the target is also a concept in this vocabulary.
     * @param uri - The URI of the concept
     * @returns Array of typed relationships with target URI, label, and type
     */
    getConceptRelationships(uri) {
        const results = [];
        const seen = new Set();
        const add = (targetUri, type) => {
            const key = `${type}:${targetUri}`;
            if (seen.has(key) || !this.hasConcept(targetUri))
                return;
            seen.add(key);
            const targetLabel = this.getFirstLiteral(targetUri, skos.prefLabel) ||
                (targetUri.includes('#') ? targetUri.split('#').pop() : targetUri.split('/').pop() || targetUri);
            results.push({ targetUri, targetLabel, type });
        };
        // Directional: concept as subject, plus inverse deduction (matching getConcept() logic)
        for (const q of this.store.getQuads(uri, skos.broader, null, null))
            add(q.object.value, 'broader');
        for (const q of this.store.getQuads(null, skos.narrower, uri, null))
            add(q.subject.value, 'broader');
        for (const q of this.store.getQuads(uri, skos.narrower, null, null))
            add(q.object.value, 'narrower');
        for (const q of this.store.getQuads(null, skos.broader, uri, null))
            add(q.subject.value, 'narrower');
        // Symmetric: concept as subject or object
        for (const q of this.store.getQuads(uri, skos.related, null, null))
            add(q.object.value, 'related');
        for (const q of this.store.getQuads(null, skos.related, uri, null))
            add(q.subject.value, 'related');
        const exactMatch = `${skos.namespace}exactMatch`;
        for (const q of this.store.getQuads(uri, exactMatch, null, null))
            add(q.object.value, 'exactMatch');
        for (const q of this.store.getQuads(null, exactMatch, uri, null))
            add(q.subject.value, 'exactMatch');
        const closeMatch = `${skos.namespace}closeMatch`;
        for (const q of this.store.getQuads(uri, closeMatch, null, null))
            add(q.object.value, 'closeMatch');
        for (const q of this.store.getQuads(null, closeMatch, uri, null))
            add(q.subject.value, 'closeMatch');
        const broadMatch = `${skos.namespace}broadMatch`;
        for (const q of this.store.getQuads(uri, broadMatch, null, null))
            add(q.object.value, 'broadMatch');
        for (const q of this.store.getQuads(null, broadMatch, uri, null))
            add(q.subject.value, 'narrowMatch');
        const narrowMatch = `${skos.namespace}narrowMatch`;
        for (const q of this.store.getQuads(uri, narrowMatch, null, null))
            add(q.object.value, 'narrowMatch');
        for (const q of this.store.getQuads(null, narrowMatch, uri, null))
            add(q.subject.value, 'broadMatch');
        const relatedMatch = `${skos.namespace}relatedMatch`;
        for (const q of this.store.getQuads(uri, relatedMatch, null, null))
            add(q.object.value, 'relatedMatch');
        for (const q of this.store.getQuads(null, relatedMatch, uri, null))
            add(q.subject.value, 'relatedMatch');
        return results;
    }
    /**
     * Finds all external URIs — URIs referenced in relationship predicates
     * (broader, narrower, related, SKOS mapping properties) but not typed
     * as skos:Concept in this vocabulary's store.
     *
     * @returns Array of external URIs with the local concepts and predicates that reference them
     */
    getExternalReferences() {
        const predicates = [
            skos.broader,
            skos.narrower,
            skos.related,
            `${skos.namespace}exactMatch`,
            `${skos.namespace}closeMatch`,
            `${skos.namespace}broadMatch`,
            `${skos.namespace}narrowMatch`,
            `${skos.namespace}relatedMatch`,
        ];
        const externalMap = new Map();
        for (const predicate of predicates) {
            for (const quad of this.store.getQuads(null, predicate, null, null)) {
                const targetUri = quad.object.value;
                if (!this.hasConcept(targetUri)) {
                    if (!externalMap.has(targetUri))
                        externalMap.set(targetUri, []);
                    externalMap.get(targetUri).push({ conceptUri: quad.subject.value, predicate });
                }
            }
        }
        return Array.from(externalMap.entries()).map(([uri, referencedBy]) => ({ uri, referencedBy }));
    }
    /**
     * Retrieves all SKOS Collections in the vocabulary.
     * @returns Array of collections with URI, multilingual labels, and member concept URIs
     */
    getCollections() {
        // Collect URIs typed as skos:Collection OR iso-thes:ConceptGroup
        // (N3 store has no RDFS inference, so subclass types must be queried explicitly)
        const collectionUriSet = new Set();
        for (const q of this.store.getQuads(null, rdf.type, skos.Collection, null)) {
            collectionUriSet.add(q.subject.value);
        }
        for (const q of this.store.getQuads(null, rdf.type, isoThes.ConceptGroup, null)) {
            collectionUriSet.add(q.subject.value);
        }
        return Array.from(collectionUriSet).map((uri) => {
            const labels = this.getSubjectLiterals(uri, [skos.prefLabel, skos.altLabel, skos.hiddenLabel]);
            const notes = this.getSubjectLiterals(uri, [skos.definition, skos.scopeNote, skos.note]);
            const notationQuads = this.store.getQuads(uri, skos.notation, null, null);
            const notation = notationQuads.length > 0 ? notationQuads[0].object.value : undefined;
            const memberQuads = this.store.getQuads(uri, skos.member, null, null);
            const memberUris = memberQuads.map((mq) => mq.object.value);
            // Resolve parent group: iso-thes:superGroup (forward) or iso-thes:subGroup (inverse)
            let superGroupUri;
            const superGroupQuads = this.store.getQuads(uri, isoThes.superGroup, null, null);
            if (superGroupQuads.length > 0) {
                superGroupUri = superGroupQuads[0].object.value;
            }
            else {
                const inverseQuads = this.store.getQuads(null, isoThes.subGroup, uri, null);
                if (inverseQuads.length > 0) {
                    superGroupUri = inverseQuads[0].subject.value;
                }
            }
            return { uri, labels, notes, notation, memberUris, superGroupUri };
        });
    }
    /**
     * Retrieves the member Concepts of a SKOS Collection.
     * @param collectionUri - The URI of the collection
     * @returns Array of Concept instances that are members of the collection
     */
    getCollectionMembers(collectionUri) {
        const memberQuads = this.store.getQuads(collectionUri, skos.member, null, null);
        return memberQuads
            .map((q) => q.object.value)
            .filter((uri) => this.hasConcept(uri))
            .map((uri) => this.getConcept(uri));
    }
}
export { Vocabulary };
